import t from "@repo/localization/string.macro";
import { ReportAccount as CanadaReportAccountPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/credit/report_account_pb";
import Protobuf from "@repo/protobuf/utils";
import Address from "@repo/utils/Address";
import Case from "@repo/utils/Case";
import Enum from "@repo/utils/Enum";

type ReportAccount = {
  id: ReportAccount.Id;
  name: string;
  type: ReportAccount.Type;
  openedAt: Date;
  closedAt?: Date;
  lastReportedAt: Date;
  lastActivityAt: Date;
  currentBalanceCents: number;
  creditLimitAmountCents: number;
  pastDueAmountCents: number;
  lastPaymentAmountCents: number;
  paymentHistory: ReportAccount.Payment[];
  contactInfo: ReportAccount.ContactInfo;
  description: string;
};

namespace ReportAccount {
  export type Id = string & {};

  export const types = [
    "other",
    "installment",
    "revolving",
    "mortgage",
    "inquiry",
    "collection",
  ] as const;
  export type Type = (typeof types)[number];

  export namespace Type {
    const iconByType: Record<Type, string> = {
      revolving: "💳",
      installment: "💰",
      mortgage: "🏡",
      inquiry: "",
      collection: "",
      other: "💵",
    };
    export const icon = (type: Type) => iconByType[type];
    export const format = (() => {
      const map = {
        installment: () => t`Installment`,
        revolving: () => t`Revolving`,
        mortgage: () => t`Mortgage`,
        inquiry: () => t`Inquiry`,
        collection: () => t`Collection`,
        other: () => t`Other`,
      } satisfies Record<Type, () => string>;
      return (type: Type) => map[type]();
    })();
  }
  export namespace Payment {
    export type Status =
      | "other"
      | "current"
      | "unavailable"
      | "lateLessThan30Days"
      | "late30Days"
      | "late60Days"
      | "late90Days"
      | "late120Days"
      | "chargeOff"
      | "collection"
      | "repossession"
      | "zeroBalAndCurrAcct"
      | "notReported"
      | "collectionChargeoff";
  }

  export type Payment = {
    date: Date;
    status: Payment.Status;
  };

  export type ContactInfo = {
    name: string;
    address: Address;
    phone: string;
  };

  export namespace Preview {
    export type MeasureType =
      | "utilization"
      | "balance"
      | "onTimePayments"
      | "missedPayments"
      | "accountCode"
      | "creditAge"
      | "collections"
      | "accountStatus";
  }

  export type Preview = {
    id: ReportAccount.Id;
    name: string;
    text: string;
    measureType: Preview.MeasureType;
    measureValue: string;
    isOpen: boolean;
    accountType: ReportAccount.Type;
    isKikoffAccount: boolean;
    paymentHistory: ReportAccount.Payment[];
  };
}

export default ReportAccount;

export namespace ReportAccountCanada {
  export const normalize = (proto: CanadaReportAccountPb): ReportAccount => {
    const type = Case.fromConstant(
      Enum.keyOf(CanadaReportAccountPb.Type, proto.type)
    ).toCamel();
    return {
      ...proto,
      type,
      openedAt: Protobuf.Timestamp.toDate(proto.openedAt!),
      closedAt: Protobuf.Timestamp.toDate(proto.closedAt),
      lastReportedAt: Protobuf.Timestamp.toDate(proto.lastReportedAt!),
      lastActivityAt: Protobuf.Timestamp.toDate(proto.lastActivityAt!),
      paymentHistory: proto.paymentHistory.map(Payment.normalize),
      contactInfo: proto.contactInfo as ReportAccount.ContactInfo,
    };
  };
  export namespace Payment {
    export const normalize = (
      proto: CanadaReportAccountPb.Payment
    ): ReportAccount.Payment => {
      const status = Case.fromConstant(
        Enum.keyOf(CanadaReportAccountPb.Payment.Status, proto.status)
      ).toCamel();
      return {
        date: Protobuf.Timestamp.toDate(proto.date!),
        status,
      };
    };
  }
  export namespace Preview {
    export const normalize = (
      proto: CanadaReportAccountPb.Preview
    ): ReportAccount.Preview => {
      const measureType = Case.fromConstant(
        Enum.keyOf(CanadaReportAccountPb.Preview.MeasureType, proto.measureType)
      ).toCamel();

      if (measureType === "unknown")
        throw new Error("Unexpected UNKNOWN measureType.");

      const accountType = Case.fromConstant(
        Enum.keyOf(CanadaReportAccountPb.Type, proto.accountType)
      ).toCamel();

      return {
        ...proto,
        measureType,
        accountType,
        paymentHistory: proto.paymentHistory.map(Payment.normalize),
      };
    };
  }
}
